/** Farther than this from where it is drawn (sim units) is a jump (a blink, a respawn, a dig coming up): it snaps, it doesn't glide. */
const SNAP = 900;
/** How much of the way a glide covers per tick: 0.8 spreads it over 1.25 ticks. */
export const GLIDE = 0.8;

/**
 * Each tick, what moves glides from where it is drawn now to where the tick put it, over a little more than a
 * tick (`GLIDE`). Starting from the screen, not from the last tick, two ticks landing in one frame speed it up
 * instead of jumping it, and over a little more than a tick, one landing a bit late doesn't stop it dead (live
 * play has both, from the network). At a steady pace it walks evenly, a quarter of a tick behind. The poses
 * and the nightmares' boil stay on the 12 Hz beat: only the ground they cover glides.
 */
export class Track {
  /** Where each thing was drawn when the tick turned (it glides from there). */
  private from = new Map<number | string, number>();
  /** Where each thing is drawn now. */
  private shown = new Map<number | string, number>();
  /** Something on this track moved since the last tick. */
  moving = false;
  x = 0;
  y = 0;

  /** A new tick: it glides from where it is drawn. */
  turn(): void {
    const t = this.from;
    this.from = this.shown;
    this.shown = t;
    this.shown.clear();
    this.moving = false;
  }

  /**
   * Its position `alpha` of the way from where it was drawn at the tick to where it is now, in `x`/`y`.
   * `fresh`: this is the first draw of the tick.
   */
  at(id: number | string, x: number, y: number, alpha: number, fresh: boolean): this {
    this.x = x;
    this.y = y;
    const p = this.from.get(id);
    if (p !== undefined) {
      const px = unpackX(p);
      const py = unpackY(p);
      const dx = x - px;
      const dy = y - py;
      if ((dx !== 0 || dy !== 0) && dx <= SNAP && dx >= -SNAP && dy <= SNAP && dy >= -SNAP) {
        if (fresh) this.moving = true;
        this.x = Math.round(px + dx * alpha);
        this.y = Math.round(py + dy * alpha);
      }
    }
    this.shown.set(id, pack(this.x, this.y));
    return this;
  }
}

// Sim coordinates are integers well inside ±2^25: two of them fit exactly in one double.
const OFF = 1 << 25;
const pack = (x: number, y: number): number => (x + OFF) * (OFF * 2) + (y + OFF);
const unpackX = (p: number): number => Math.floor(p / (OFF * 2)) - OFF;
const unpackY = (p: number): number => (p % (OFF * 2)) - OFF;
